import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getApplications } from "@/lib/api";
import { useAppStore } from "@/store/appStore";
import { statusMeta } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ArrowDown, ChevronDown, Globe, Monitor, Network, ShieldHalf, Layers, Database, X, MousePointerClick,
  FileText, Search,
} from "lucide-react";

/* ---------------- dependency graph (canonical ids) ---------------- */
const LABELS = {
  SMART_METERS: "Smart Meters", SCADA: "SCADA", ADMS: "ADMS / OMS", SAP: "SAP ECC / CRM", EAI: "EAI",
  DATAPOWER: "DataPower", CORP_NET: "Corporate Network Infrastructure", SAP_DATA: "SAP Data Services",
  ECDC: "ECDC Internet Firewall", OT_FAILOVER: "Outage Tracker failover", IRIS: "IRIS / NetCadOps",
  OUTAGE_TRACKER: "Outage Tracker", PAS: "PAS (AMS)", MOBILE: "Mobile Data Service Suite",
  EMSWEB: "EMSweb", REPORT_OUTAGE: "Report an Outage", CUSTOMER_NOTIF: "Customer Notifications",
};
// "A feeds B" => EDGES[A] contains B (B depends on A / B is downstream of A)
const EDGES = {
  SMART_METERS: ["SCADA"],
  SCADA: ["ADMS"],
  ADMS: ["OUTAGE_TRACKER", "REPORT_OUTAGE", "MOBILE", "IRIS", "PAS", "EMSWEB", "CUSTOMER_NOTIF"],
  SAP: ["OUTAGE_TRACKER", "REPORT_OUTAGE", "ADMS", "CUSTOMER_NOTIF"],
  EAI: ["OUTAGE_TRACKER", "ADMS", "MOBILE", "IRIS"],
  DATAPOWER: ["REPORT_OUTAGE"],
  OT_FAILOVER: ["OUTAGE_TRACKER"],
  PAS: ["CUSTOMER_NOTIF"],
};
function downstreamOf(id) {
  const seen = new Set();
  const stack = [...(EDGES[id] || [])];
  while (stack.length) {
    const x = stack.pop();
    if (seen.has(x)) continue;
    seen.add(x);
    (EDGES[x] || []).forEach((y) => { if (!seen.has(y)) stack.push(y); });
  }
  return seen;
}

const HUB_ID = {
  "Corporate Network Infrastructure": "CORP_NET", "SAP ECC / CRM": "SAP", "SCADA": "SCADA",
  "ADMS – OMS / NetCadOps": "ADMS", "DataPower (API Gateway)": "DATAPOWER",
  "SAP Data Services Data Pipelines": "SAP_DATA", "ECDC Internet Firewall": "ECDC", "EAI": "EAI",
};
const APP_ID = {
  "Outage Tracker": "OUTAGE_TRACKER", "Report an Outage": "REPORT_OUTAGE", "ADMS (NetCadOps / OMS)": "ADMS",
  "Mobile Data Service Suite": "MOBILE", "Iris": "IRIS", "PAS (AMS)": "PAS", "EMSweb": "EMSWEB",
  "Customer Notifications (Phone / SMS / Email)": "CUSTOMER_NOTIF",
};
const CHIP_ID = {
  "ADMS": "ADMS", "EAI": "EAI", "Outage Tracker failover": "OT_FAILOVER", "SAP": "SAP",
  "DataPower": "DATAPOWER", "SCADA (ping source)": "SCADA", "SAP (customer master)": "SAP",
};

/* ---------------- nodes ---------------- */
const TRUNK_STYLES = {
  grey: { bg: "#F1F3F6", border: "#D5DBE3", title: "#3A4553" },
  green: { bg: "#EAF7EF", border: "#00A651", title: "#0B5B33" },
  greenStrong: { bg: "#E1F4E9", border: "#00A651", title: "#0B5B33" },
};

function TrunkNode({ title, sub, variant = "grey", testid, onClick, stateCls }) {
  const s = TRUNK_STYLES[variant];
  return (
    <div data-testid={testid} onClick={onClick}
      className={cn("w-full max-w-sm rounded-xl border-2 px-5 py-4 text-center cursor-pointer transition-all duration-300 hover:shadow-md", stateCls)}
      style={{ backgroundColor: s.bg, borderColor: s.border }}>
      <div className="font-bold text-base leading-tight" style={{ color: s.title }}>{title}</div>
      {sub && <div className="text-xs text-cnp-textMuted mt-1">{sub}</div>}
    </div>
  );
}

function VArrow() {
  return <ArrowDown className="text-cnp-grey my-1.5" size={22} strokeWidth={2} />;
}

function LeafNode({ title, sub, testid, onClick, stateCls, fill }) {
  return (
    <div data-testid={testid} onClick={onClick}
      className={cn("rounded-xl border border-cnp-border px-3 py-4 flex flex-col items-center text-center transition-all duration-300 hover:shadow-md cursor-pointer", !fill && "bg-white", stateCls)}
      style={fill ? { backgroundColor: fill } : undefined}>
      <div className="font-bold text-cnp-navy text-sm leading-tight">{title}</div>
      {sub && <div className="text-xs text-cnp-textMuted mt-0.5">{sub}</div>}
    </div>
  );
}

const LEAVES = [
  { title: "IRIS", sub: "NetCadOps", testid: "leaf-iris", id: "IRIS", fill: "#C2C8D1" },
  { title: "Outage", sub: "Tracker", testid: "leaf-outage-tracker", id: "OUTAGE_TRACKER", fill: "#C2C8D1" },
  { title: "PAS", sub: "AMS", testid: "leaf-pas", id: "PAS", fill: "#C2C8D1" },
  { title: "Mobile Data", sub: "Service Suite", testid: "leaf-mobile", id: "MOBILE", fill: "#C2C8D1" },
  { title: "EMSweb", sub: "OEP", testid: "leaf-emsweb", id: "EMSWEB", fill: "#C2C8D1" },
];

function DownstreamConnectors() {
  const n = LEAVES.length;
  return (
    <div className="hidden md:block" data-testid="downstream-connectors" aria-hidden>
      <div className="flex justify-center">
        <div className="h-6 border-l-2 border-dashed border-cnp-green/50" />
      </div>
      <div className="grid grid-cols-5 gap-3 h-8">
        {LEAVES.map((l, i) => {
          const first = i === 0;
          const last = i === n - 1;
          return (
            <div key={l.testid} className="relative">
              <div className={cn("absolute top-0 border-t-2 border-dashed border-cnp-green/50",
                first ? "left-1/2 right-0" : last ? "left-0 right-1/2" : "left-0 right-0")} />
              <div className="absolute top-0 bottom-2 left-1/2 -translate-x-1/2 border-l-2 border-dashed border-cnp-green/50" />
              <ChevronDown size={16} strokeWidth={3} className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-cnp-green" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SapBadge = () => (
  <span className="h-7 px-1.5 rounded bg-cnp-blue text-white text-[11px] font-extrabold flex items-center justify-center">SAP</span>
);

const HUBS = [
  { icon: Globe, name: "Corporate Network Infrastructure", direct: 14, transitive: 25, hard: 1, kw: ["corporate network"] },
  { icon: SapBadge, name: "SAP ECC / CRM", direct: 13, transitive: 16, hard: 12, kw: ["sap ecc"] },
  { icon: Monitor, name: "SCADA", direct: 2, transitive: 10, hard: 2, kw: ["scada"] },
  { icon: Database, name: "ADMS – OMS / NetCadOps", direct: 8, transitive: 8, hard: 7, kw: ["adms"] },
  { icon: Network, name: "DataPower (API Gateway)", direct: 6, transitive: 8, hard: 5, kw: ["datapower", "data power"] },
  { icon: Database, name: "SAP Data Services Data Pipelines", direct: 3, transitive: 3, hard: 1, kw: ["data services"] },
  { icon: ShieldHalf, name: "ECDC Internet Firewall", direct: 3, transitive: 3, hard: 1, kw: ["ecdc", "firewall"] },
  { icon: Layers, name: "EAI", direct: 3, transitive: 3, hard: 1, kw: ["eai"] },
];

function HubIcon({ icon: Icon }) {
  if (Icon === SapBadge) return <SapBadge />;
  return (
    <span className="h-7 w-7 rounded-lg flex items-center justify-center bg-cnp-blue/10 text-cnp-blue">
      <Icon size={17} strokeWidth={2} />
    </span>
  );
}

const HARD_DEPS = [
  { app: "Outage Tracker", requires: ["ADMS", "EAI", "Outage Tracker failover", "SAP"], note: "all the four systems should be active and up and running" },
  { app: "Report an Outage", requires: ["DataPower", "SAP", "ADMS"] },
  { app: "ADMS (NetCadOps / OMS)", requires: ["SCADA (ping source)", "SAP", "EAI"], note: "without SCADA, ADMS is blind" },
  { app: "Mobile Data Service Suite", requires: ["ADMS", "EAI"] },
  { app: "Iris", requires: ["ADMS", "EAI"] },
  { app: "PAS (AMS)", requires: ["ADMS"] },
  { app: "EMSweb", requires: ["ADMS"] },
  { app: "Customer Notifications (Phone / SMS / Email)", requires: ["ADMS", "SAP (customer master)"] },
];

export default function DependenciesPage() {
  const [selected, setSelected] = useState(null);
  const [depSearch, setDepSearch] = useState("");
  const scenario = useAppStore((s) => s.scenario);
  const { data: appsData } = useQuery({
    queryKey: ["dep-apps", scenario],
    queryFn: () => getApplications({ scenario, page_size: 500 }),
  });
  const hubTri = useMemo(() => {
    const rows = appsData?.rows || [];
    const map = {};
    HUBS.forEach((h) => {
      const app = rows.find((r) => h.kw.some((k) => r.name.toLowerCase().includes(k)));
      if (app) map[h.name] = { score: app.score, status: app.status };
    });
    return map;
  }, [appsData]);
  const activeSet = useMemo(() => (selected ? new Set([selected, ...downstreamOf(selected)]) : null), [selected]);
  const toggle = (id) => { if (!id) return; setSelected((p) => (p === id ? null : id)); };

  const depNotes = useMemo(() => {
    const rows = (appsData?.rows || []).filter((r) => r.dependency_desc && r.dependency_desc.trim());
    const q = depSearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => (r.name + " " + r.dependency_desc).toLowerCase().includes(q))
      : rows;
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [appsData, depSearch]);
  const depNotesTotal = useMemo(
    () => (appsData?.rows || []).filter((r) => r.dependency_desc && r.dependency_desc.trim()).length,
    [appsData]
  );

  const status = (id) => {
    if (!activeSet || !id) return "none";
    if (selected === id) return "sel";
    if (activeSet.has(id)) return "down";
    return "dim";
  };
  const cardCls = (id) => {
    const s = status(id);
    return cn(s === "sel" && "ring-2 ring-cnp-navy shadow-lg -translate-y-0.5",
      s === "down" && "ring-2 ring-cnp-green shadow-md -translate-y-0.5",
      s === "dim" && "opacity-25");
  };
  const rowCls = (id) => {
    const s = status(id);
    return cn("cursor-pointer transition-colors",
      s === "sel" && "bg-cnp-navy/5 ring-1 ring-inset ring-cnp-navy",
      s === "down" && "bg-cnp-green/10",
      s === "dim" && "opacity-25",
      s === "none" && "hover:bg-cnp-bg/60");
  };
  const chipCls = (id) => {
    const s = status(id);
    return cn("cursor-pointer transition-all",
      s === "sel" && "!bg-cnp-navy !text-white",
      s === "down" && "!bg-cnp-green/20 !text-cnp-green ring-1 ring-cnp-green/40",
      s === "dim" && "opacity-30");
  };
  const downstreamCount = activeSet ? [...activeSet].filter((x) => x !== selected).length : 0;

  return (
    <div className="space-y-4 pb-16" data-testid="dependencies-page">
      {/* Selection banner / hint */}
      {selected ? (
        <div className="rounded-lg border border-cnp-green/40 bg-cnp-green/5 px-4 py-2.5 flex items-center gap-3 sticky top-[64px] z-10" data-testid="cross-highlight-banner">
          <span className="h-6 w-6 rounded-full bg-cnp-green text-white flex items-center justify-center shrink-0 text-xs font-bold">{downstreamCount}</span>
          <span className="text-sm text-cnp-text">
            Showing everything downstream of <b>{LABELS[selected]}</b> —
            <b className="ml-1">{downstreamCount}</b> app{downstreamCount === 1 ? "" : "s"} it feeds are highlighted.
          </span>
          <button onClick={() => setSelected(null)} data-testid="clear-highlight"
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-cnp-green hover:text-cnp-navy transition-colors">
            <X size={14} /> Clear
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-cnp-border bg-white px-4 py-2.5 flex items-center gap-2 text-sm text-cnp-textMuted" data-testid="cross-highlight-hint">
          <MousePointerClick size={16} className="text-cnp-blue" />
          Click any system (a node, a hub, or a dependency chip) to light up everything it feeds across all views.
        </div>
      )}

      {/* Spine */}
      <div className="rounded-xl border border-cnp-border bg-white p-6" data-testid="spine-diagram">
        <div className="mb-6">
          <h2 className="text-lg font-extrabold text-cnp-navy tracking-tight uppercase">The Spine: CenterPoint's Storm Response Flow</h2>
          <p className="text-sm text-cnp-textMuted mt-1">One chain carries most of CenterPoint's storm response.</p>
        </div>

        {/* Vertical trunk: smart meters -> SCADA -> ADMS */}
        <div className="flex flex-col items-center">
          <TrunkNode title="~3 million smart meters" sub="ping every 15 minutes" variant="grey" testid="spine-smart-meters"
            onClick={() => toggle("SMART_METERS")} stateCls={cardCls("SMART_METERS")} />
          <VArrow />
          <TrunkNode title="SCADA" sub="Event Hub" variant="green" testid="spine-scada"
            onClick={() => toggle("SCADA")} stateCls={cardCls("SCADA")} />
          <VArrow />
          <TrunkNode title="ADMS / OMS (NetCadOps)" sub="source of truth for outages" variant="greenStrong" testid="spine-adms"
            onClick={() => toggle("ADMS")} stateCls={cardCls("ADMS")} />
        </div>

        <DownstreamConnectors />

        <div className="mt-2">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {LEAVES.map((l) => (
              <LeafNode key={l.testid} title={l.title} sub={l.sub} testid={l.testid} fill={l.fill}
                onClick={() => toggle(l.id)} stateCls={cardCls(l.id)} />
            ))}
          </div>

          {/* PAS (middle leaf) -> Customer notification, centered under PAS */}
          <div className="mt-2 flex flex-col items-center">
            <ArrowDown className="text-cnp-grey" size={22} />
            <div onClick={() => toggle("CUSTOMER_NOTIF")}
              className={cn("mt-1.5 w-full max-w-sm rounded-xl border-2 px-5 py-4 text-center cursor-pointer transition-all duration-300 hover:shadow-md", cardCls("CUSTOMER_NOTIF"))}
              style={{ backgroundColor: "#FBF1DD", borderColor: "#C77F0A" }}
              data-testid="spine-customer-notification">
              <div className="font-bold text-base" style={{ color: "#8A5A00" }}>Customer notification</div>
              <div className="text-xs mt-1" style={{ color: "#A56A00" }}>phone · SMS · email</div>
            </div>
          </div>
        </div>
      </div>

      {/* Blast radius */}
      <div className="rounded-xl border border-cnp-border bg-white p-6" data-testid="blast-radius">
        <div className="mb-4">
          <h2 className="text-lg font-extrabold text-cnp-navy tracking-tight uppercase">Hub Systems: Blast Radius</h2>
          <p className="text-sm text-cnp-textMuted mt-1">Direct vs. transitive impact based on dependency chains.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-cnp-border text-cnp-textMuted">
                <th className="text-left py-2.5 pr-3 font-semibold uppercase text-xs tracking-wide">Upstream Component</th>
                <th className="py-2.5 px-3 font-semibold uppercase text-xs tracking-wide text-center text-cnp-navy">TRI</th>
                <th className="py-2.5 px-3 font-semibold uppercase text-xs tracking-wide text-center text-cnp-blue">Direct<br/>Dependants</th>
                <th className="py-2.5 px-3 font-semibold uppercase text-xs tracking-wide text-center text-cnp-purple">Transitive<br/>Reach</th>
                <th className="py-2.5 px-3 font-semibold uppercase text-xs tracking-wide text-center text-cnp-red">Hard<br/>Edges</th>
              </tr>
            </thead>
            <tbody>
              {HUBS.map((h) => {
                const id = HUB_ID[h.name];
                return (
                  <tr key={h.name} onClick={() => toggle(id)} data-testid={`hub-row-${id}`}
                    className={cn("border-b border-cnp-border/60", rowCls(id))}>
                    <td className="py-3 pr-3">
                      <span className="flex items-center gap-3">
                        <HubIcon icon={h.icon} />
                        <span className="font-semibold text-cnp-navy">{h.name}</span>
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {hubTri[h.name]
                        ? <span className="inline-flex items-center gap-1.5 tnum font-bold" style={{ color: statusMeta(hubTri[h.name].status).color }}>
                            <span className="h-2 w-2 rounded-full" style={{ background: statusMeta(hubTri[h.name].status).color }} />
                            {hubTri[h.name].score}%
                          </span>
                        : <span className="text-cnp-textMuted">—</span>}
                    </td>
                    <td className="py-3 px-3 text-center tnum font-bold text-cnp-text">{h.direct}</td>
                    <td className="py-3 px-3 text-center tnum font-bold text-cnp-text">{h.transitive}</td>
                    <td className="py-3 px-3 text-center tnum font-bold" style={{ color: h.hard >= 5 ? "#D0021B" : "#1F2937" }}>{h.hard}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-cnp-textMuted mt-3">
          <b>Direct dependants</b> depend on the component directly; <b>transitive reach</b> includes everything downstream through the chain; <b>hard edges</b> are dependencies with no failover.
        </p>
      </div>

      {/* Hard dependencies */}
      <div className="rounded-xl border border-cnp-border bg-white p-6" data-testid="hard-dependencies">
        <div className="mb-4">
          <h2 className="text-lg font-extrabold text-cnp-navy tracking-tight uppercase">Hard Dependencies</h2>
          <p className="text-sm text-cnp-textMuted mt-1">Systems that must be up for each application to function during a storm.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-cnp-border text-cnp-blue">
                <th className="text-left py-2.5 pr-4 font-bold uppercase text-xs tracking-wide w-[38%]">Application</th>
                <th className="text-left py-2.5 pl-4 font-bold uppercase text-xs tracking-wide border-l border-cnp-border">Requires (Hard Dependencies)</th>
              </tr>
            </thead>
            <tbody>
              {HARD_DEPS.map((d) => {
                const appId = APP_ID[d.app];
                const s = status(appId);
                return (
                  <tr key={d.app} data-testid={`harddep-row-${appId}`}
                    className={cn("border-b border-cnp-border/60 align-top", s === "dim" ? "opacity-25" : s === "down" ? "bg-cnp-green/10" : s === "sel" ? "bg-cnp-navy/5 ring-1 ring-inset ring-cnp-navy" : "")}>
                    <td onClick={() => toggle(appId)} className="py-3.5 pr-4 font-bold text-cnp-navy cursor-pointer hover:text-cnp-blue">{d.app}</td>
                    <td className="py-3.5 pl-4 border-l border-cnp-border">
                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
                        {d.requires.map((rq, i) => {
                          const cid = CHIP_ID[rq];
                          return (
                            <React.Fragment key={rq}>
                              {i > 0 && <span className="text-cnp-grey">•</span>}
                              <span onClick={() => toggle(cid)} data-testid={`chip-${cid}-${appId}`}
                                className={cn("inline-flex rounded-md bg-cnp-blue/10 text-cnp-blue font-semibold px-2 py-0.5 text-xs", chipCls(cid))}>{rq}</span>
                            </React.Fragment>
                          );
                        })}
                      </div>
                      {d.note && <p className="text-xs text-cnp-textMuted italic mt-2">"{d.note}"</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Application dependency notes — from the workbook "Description & Dependencies" column */}
      <div className="rounded-xl border border-cnp-border bg-white p-6" data-testid="dependency-notes">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-extrabold text-cnp-navy tracking-tight uppercase flex items-center gap-2">
              <FileText size={18} className="text-cnp-blue" /> Application Dependency Notes
            </h2>
            <p className="text-sm text-cnp-textMuted mt-1">
              Based on the workbook's "Description &amp; Dependencies" column (Raja Wuyyuru call, 08/18/2026), edited for clarity — {depNotesTotal} applications documented.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-cnp-textMuted" />
            <input
              data-testid="dependency-notes-search"
              value={depSearch}
              onChange={(e) => setDepSearch(e.target.value)}
              placeholder="Search apps or dependencies…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-cnp-border bg-white focus:outline-none focus:ring-2 focus:ring-cnp-blue/40"
            />
          </div>
        </div>
        {depNotes.length === 0 ? (
          <p className="text-sm text-cnp-textMuted py-8 text-center" data-testid="dependency-notes-empty">
            {depNotesTotal === 0 ? "No dependency descriptions found in the current workbook." : "No applications match your search."}
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 max-h-[520px] overflow-y-auto pr-1">
            {depNotes.map((r) => (
              <div key={r.application_id} data-testid={`dep-note-${r.application_id}`}
                className="rounded-lg border border-cnp-border bg-cnp-bg/40 p-4 transition-all hover:shadow-md hover:bg-white">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <span className="font-bold text-cnp-navy text-sm leading-tight">{r.name}</span>
                  {r.status && (
                    <span className="shrink-0 inline-flex items-center gap-1 tnum text-xs font-bold" style={{ color: statusMeta(r.status).color }}>
                      <span className="h-2 w-2 rounded-full" style={{ background: statusMeta(r.status).color }} />
                      {r.score != null ? `${r.score}%` : "—"}
                    </span>
                  )}
                </div>
                <p className="text-[13px] text-cnp-text leading-relaxed">{r.dependency_desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-cnp-textMuted px-1" data-testid="dependencies-note">
        Dependency descriptions above are drawn from the tracking workbook and lightly edited for readability. An interactive graph (upstream / downstream traversal, hops slider, single points of failure) will build on this dataset next.
      </p>
    </div>
  );
}
